import type { AlertViewEvents } from './events';
import type { Alert, AlertViewContext, AlertViewScope } from './types';

/**
 * Add a new alert to the queue
 */
export const showAlertAction = ({ context, event }: AlertViewScope): Partial<AlertViewContext> => {
  const showEvent = event as Extract<AlertViewEvents, { type: 'ON_SHOW_ALERT' }>;

  const newAlert: Alert = {
    id: crypto.randomUUID(),
    createdAt: new Date(),
    ...showEvent.payload,
  };

  return {
    alerts: [...context.alerts, newAlert],
  };
};

/**
 * Clear the active alert (no alert showing)
 */
export const hideAlertAction = (): Partial<AlertViewContext> => {
  return {
    activeAlert: null,
  };
};

/**
 * Dismiss a specific alert by ID (remove from queue)
 */
export const dismissAlertAction = ({
  context,
  event,
}: AlertViewScope): Partial<AlertViewContext> => {
  const dismissEvent = event as Extract<AlertViewEvents, { type: 'ON_DISMISS_ALERT' }>;
  const { id } = dismissEvent.payload;

  // If dismissing the active alert, clear it
  if (context.activeAlert?.id === id) {
    return {
      alerts: context.alerts.filter((alert) => alert.id !== id),
      activeAlert: null,
    };
  }

  // Otherwise just remove from queue
  return {
    alerts: context.alerts.filter((alert) => alert.id !== id),
  };
};

/**
 * Move to the next alert in the queue
 */
export const nextAlertAction = ({ context }: AlertViewScope): Partial<AlertViewContext> => {
  const remainingAlerts = context.alerts.filter((alert) => alert.id !== context.activeAlert?.id);

  return {
    alerts: remainingAlerts,
    activeAlert: remainingAlerts[0] || null,
  };
};

/**
 * Set the active alert and remove it from the queue
 */
export const setActiveAlertAction = ({ context }: AlertViewScope): Partial<AlertViewContext> => {
  if (context.alerts.length === 0) {
    return {
      activeAlert: null,
    };
  }

  const [nextAlert, ...remaining] = context.alerts;

  return {
    alerts: remaining,
    activeAlert: nextAlert,
  };
};
